import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';

interface MetaSocialImage {
  data?: {
    attributes?: {
      url?: string;
      width?: number;
      height?: number;
    }
  }
}

interface MetaSocialNetwork {
  socialNetwork: string;
  title: string;
  description: string;
  image?: MetaSocialImage;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private meta = inject(Meta);
  private titleService = inject(Title);

  pluginSeo : any;

  constructor() {}

  /**
   * initializes SEO by parsing the provided SEO data
   * @param seoData - structured SEO data from CMS
   */
  public initializeSeo(seoData: any): void {
    if (!seoData || seoData.length === 0) {
        console.warn('No SEO data provided');
        return;
    }

    this.pluginSeo = seoData;
    if (this.pluginSeo) {
        this.updateMetaTags(this.pluginSeo);
        this.updateSocialTags(this.pluginSeo.metaSocial);
        this.updateStructuredData(this.pluginSeo.structuredData);
        if (this.pluginSeo?.Google) {
            this.setupGoogleAnalytics(this.pluginSeo?.Google?.googleAnalyticsId);
        }
    } else {
        console.warn('No valid SEO configuration found in pluginSeo');
    }
}


private updateMetaTags(tags: any): void {
  const robotsContent = tags?.metaRobots?.replace(/.*content="([^"]+).*/, '$1') || 'index, follow';

  this.titleService.setTitle(tags?.metaTitle || 'Conome - Merre zyren me vete me Conome base ose premium');
  this.meta.updateTag({ name: 'description', content: tags?.metaDescription || 'Default description' });
  this.meta.updateTag({ name: 'keywords', content: tags?.keywords || 'Default keywords' });
  this.meta.updateTag({ name: 'robots', content: robotsContent });
  this.meta.updateTag({ name: 'viewport', content: tags?.metaViewport || 'width=device-width, initial-scale=1.0' });
  
  if (tags?.canonicalURL) {
      this.meta.updateTag({ name: 'canonical', content: tags?.canonicalURL });
  }
  if(tags?.Google?.verificationToken){
    this.meta.updateTag({name: 'google-site-verification', content: tags?.Google?.verificationToken})
  }
}

private updateSocialTags(metaSocial: MetaSocialNetwork[]): void {
  if (!Array.isArray(metaSocial)) return;

  const defaultTags = [
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Conome - Merre zyren me vete me Conome base ose premium' },
    { property: 'og:url', content: this.getCanonicalUrl() },
    { property: 'og:title', content: this.pluginSeo.metaTitle },
    { property: 'og:description', content: this.pluginSeo.metaDescription }
  ];

  const defaultImageUrl = this.getAbsoluteUrl(this.pluginSeo.metaImage?.data?.attributes?.url);
  if (defaultImageUrl) {
    defaultTags.push({ property: 'og:image', content: defaultImageUrl });
    
    const mainImage = this.pluginSeo.metaImage?.data?.attributes;
    if (mainImage?.width && mainImage?.height) {
      defaultTags.push(
        { property: 'og:image:width', content: mainImage.width.toString() },
        { property: 'og:image:height', content: mainImage.height.toString() }
      );
    }
  }

  defaultTags.forEach(tag => this.meta.updateTag(tag));

  metaSocial.forEach(social => {
    const network = social.socialNetwork.toLowerCase();
    const imageUrl = this.getAbsoluteUrl(social?.image?.data?.attributes?.url || '');

    if (network === 'facebook') {
      const fbTags = [
        { property: 'og:title', content: social.title },
        { property: 'og:description', content: social.description }
      ];
      
      if (imageUrl) {
        fbTags.push({ property: 'og:image', content: imageUrl });
      }

      fbTags.forEach(tag => this.meta.updateTag(tag));
    }
    else if (network === 'twitter') {
      const twitterTags = [
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: social.title },
        { name: 'twitter:description', content: social.description }
      ];

      if (imageUrl) {
        twitterTags.push({ name: 'twitter:image', content: imageUrl });
        if (social.title) {
          twitterTags.push({ name: 'twitter:image:alt', content: social.title });
        }
      }

      twitterTags.forEach(tag => this.meta.updateTag(tag));
    }
  });

  if (!metaSocial.some(social => social.socialNetwork.toLowerCase() === 'twitter')) {
    const twitterFallbackTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: this.pluginSeo.metaTitle },
      { name: 'twitter:description', content: this.pluginSeo.metaDescription }
    ];

    if (defaultImageUrl) {
      twitterFallbackTags.push({ name: 'twitter:image', content: defaultImageUrl });
    }

    twitterFallbackTags.forEach(tag => this.meta.updateTag(tag));
  }
}

private getAbsoluteUrl(imagePath: string): string {
  return imagePath?.startsWith('http') 
    ? imagePath 
    : `${environment.baseUrl}${imagePath}`;
}

private getCanonicalUrl(): string {
  return this.pluginSeo?.canonicalURL || 'https://www.conome.mk';
}

  private updateStructuredData(data: any): void {
    if (!data) return;
    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(data);
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      document.head.removeChild(existingScript);
    }
    document.head.appendChild(scriptTag);
  }

  private setupGoogleAnalytics(gaId: string): void {
    if (gaId) {
      const gaScript = document.createElement('script');
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(gaScript);

      const configScript = document.createElement('script');
      configScript.text = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${gaId}', {
          'anonymize_ip': true,
          'cookie_flags': 'SameSite=None;Secure'
        });
      `;
      document.head.appendChild(configScript);
    }
  }
}
